import type {
  OperatingParameters,
  PerfEval,
  FormItems
} from "@/composables/forms/formTypes";
import type { Override_Options_Enum } from "gql/api";

/**
 * Field name mappings for gas separation fields
 * Maps logical field names to actual database field names based on stack
 */
const GAS_SEPARATION_FIELD_NAMES = {
  upper: {
    packerInstalled: "intake_packer_installed",
    naturalGasSepOverrideType: "natural_gas_separation_override_type",
    naturalGasSepPercent: "natural_override_gas_separation_percent",
    intakeGasSepOverrideType: "intake_gas_separation_override_type",
    intakeGasSepPercent: "intake_override_gas_separation_percent"
  },
  lower: {
    packerInstalled: "lower_intake_packer_installed",
    naturalGasSepOverrideType: "lower_natural_gas_separation_override_type",
    naturalGasSepPercent: "lower_natural_override_gas_separation_percent",
    intakeGasSepOverrideType: "lower_intake_gas_separation_override_type",
    intakeGasSepPercent: "lower_intake_override_gas_separation_percent"
  }
} as const;

/**
 * Field name mappings for motor fields
 * Maps logical field names to actual database field names based on stack
 */
const MOTOR_FIELD_NAMES = {
  upper: {
    motorRerateRatio: "motor_rerate_ratio"
  },
  lower: {
    motorRerateRatio: "lower_motor_rerate_ratio"
  }
} as const;

/**
 * Field name mappings for pump fields
 * Maps logical field names to actual database field names based on stack
 */
const PUMP_FIELD_NAMES = {
  upper: {
    pumpHz: "pump_hz",
    hasCompressionPump: "has_compression_pump",
    includeCasingFrictionLoss: "include_casing_friction_loss",
    pumpOverrideGasCorrectionPercent: "pump_override_gas_correction_percent",
    pumpWearHeadCorrectionRatio: "pump_wear_head_correction_ratio",
    pumpWearBhpCorrectionRatio: "pump_wear_brake_horsepower_correction_ratio",
    useLegacyGasHeadRerate: "use_legacy_gas_head_rerate"
  },
  lower: {
    pumpHz: "lower_pump_hz",
    hasCompressionPump: "lower_has_compression_pump",
    includeCasingFrictionLoss: "lower_include_casing_friction_loss",
    pumpOverrideGasCorrectionPercent:
      "lower_pump_override_gas_correction_percent",
    pumpWearHeadCorrectionRatio: "lower_pump_wear_head_correction_ratio",
    pumpWearBhpCorrectionRatio:
      "lower_pump_wear_brake_horsepower_correction_ratio",
    useLegacyGasHeadRerate: "lower_use_legacy_gas_head_rerate"
  }
} as const;

export type GasSeparationFieldKey =
  keyof typeof GAS_SEPARATION_FIELD_NAMES.upper;
export type MotorFieldKey = keyof typeof MOTOR_FIELD_NAMES.upper;
export type PumpFieldKey = keyof typeof PUMP_FIELD_NAMES.upper;
export type Stack = "upper" | "lower" | undefined;

/**
 * Get the actual field name based on stack for gas separation fields
 * Type-safe: returns the correct field name string literal type
 */
export function getFieldName(
  field: GasSeparationFieldKey,
  stack?: Stack
):
  | (typeof GAS_SEPARATION_FIELD_NAMES.upper)[GasSeparationFieldKey]
  | (typeof GAS_SEPARATION_FIELD_NAMES.lower)[GasSeparationFieldKey] {
  const stackKey = stack === "lower" ? "lower" : "upper";
  return GAS_SEPARATION_FIELD_NAMES[stackKey][field];
}

/**
 * Get the actual field name based on stack for motor fields
 * Type-safe: returns the correct field name string literal type
 */
export function getMotorFieldName(
  field: MotorFieldKey,
  stack?: Stack
):
  | (typeof MOTOR_FIELD_NAMES.upper)[MotorFieldKey]
  | (typeof MOTOR_FIELD_NAMES.lower)[MotorFieldKey] {
  const stackKey = stack === "lower" ? "lower" : "upper";
  return MOTOR_FIELD_NAMES[stackKey][field];
}

/**
 * Get the actual field name based on stack for pump fields
 * Type-safe: returns the correct field name string literal type
 */
export function getPumpFieldName(
  field: PumpFieldKey,
  stack?: Stack
):
  | (typeof PUMP_FIELD_NAMES.upper)[PumpFieldKey]
  | (typeof PUMP_FIELD_NAMES.lower)[PumpFieldKey] {
  const stackKey = stack === "lower" ? "lower" : "upper";
  return PUMP_FIELD_NAMES[stackKey][field];
}

/**
 * Type-safe field accessor for OperatingParameters or PerfEval
 * Both types have the same gas separation fields, so this works for both
 * Returns unknown - caller should cast to appropriate type
 */
export function getFieldValue<K extends GasSeparationFieldKey>(
  params: OperatingParameters | PerfEval | null | undefined,
  field: K,
  stack?: Stack
): unknown {
  if (!params) return undefined;
  const fieldName = getFieldName(field, stack);
  // Both OperatingParameters and PerfEval have these fields in their GraphQL schema
  // TypeScript can't verify this at compile time, but both types share these fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (params as any)[fieldName];
}

/**
 * Type-safe field accessor for motor fields in OperatingParameters
 * Returns unknown - caller should cast to appropriate type
 */
export function getMotorFieldValue<K extends MotorFieldKey>(
  params: OperatingParameters | null | undefined,
  field: K,
  stack?: Stack
): unknown {
  if (!params) return undefined;
  const fieldName = getMotorFieldName(field, stack);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (params as any)[fieldName];
}

/**
 * Type-safe field accessor for pump fields in OperatingParameters
 * Returns unknown - caller should cast to appropriate type
 */
export function getPumpFieldValue<K extends PumpFieldKey>(
  params: OperatingParameters | null | undefined,
  field: K,
  stack?: Stack
): unknown {
  if (!params) return undefined;
  const fieldName = getPumpFieldName(field, stack);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (params as any)[fieldName];
}

/**
 * Type representing the gas separation fields that both OperatingParameters and PerfEval share.
 * These fields exist on both types in the GraphQL schema.
 * Using an index signature allows computed property names while maintaining value type safety.
 */
type GasSeparationUpdateFields = {
  [key: string]: Override_Options_Enum | number | null | undefined;
};

/**
 * Safely update a form with gas separation fields.
 * Both OperatingParameters and PerfEval share these fields, so this is type-safe.
 * The function accepts an object with computed property names (from getFieldName),
 * which TypeScript can't verify at compile time but are guaranteed to be valid at runtime.
 */
export function updateGasSeparationFields(
  form: FormItems["parameters"] | FormItems["performanceEvaluation"],
  updateData: GasSeparationUpdateFields
): void {
  // Both form types share these gas separation fields, so we can safely update
  // TypeScript requires this cast because it can't verify the union type compatibility
  // at compile time, but at runtime both types have these fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (form.update as (data: any) => void)(updateData);
}
